import React, { useEffect, useState } from "react";
import {
  PackageSearch,
  ShieldCheck,
  ClipboardCheck,
  ListChecks,
  FileText,
  BookOpen,
  FlaskConical,
  Settings,
  Calculator,
  Cog,
} from "lucide-react";
import {
  get_actionlist_data,
  get_all_testing_type,
  get_calculation_field,
  get_cleaning_type_for_line,
  get_clearence_type,
  get_departments,
  // get_device_details,
  get_machine_types,
  get_material_inventory,
  get_parameters,
  get_sensor_list,
  get_units,
  get_register_machine,
  get_register_by_machine,
} from "./mbmr_api";
import { get_list_of_setting } from "../../../utils/api";
import Ingredients from "./Ingredients";
import Monitoring from "./Monitoring";
import Product from "./Product";
import ActionList from "./ActionList";
import Checklist from "./Checklist";
import Instructions from "./Instructions";
import SamplingForm from "./Sampling";
import LineClearanceForm from "./LineClearance";
import ApiCalculationForm from "./APICalculation";
import MachineSetup from "./MachineSetup";
import { Card, ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";
import Icons from "@/components/third-party/Icons";
import { useSubOperationAlias } from '@/hooks/useAppSetting';

const TABS = [
  { id: "ingredients", label: "Ingredients", icon: PackageSearch },
  { id: "monitoring", label: "Monitoring", icon: ShieldCheck },
  { id: "product", label: "Product", icon: ClipboardCheck },
  { id: "action-list", label: "Action List", icon: ListChecks },
  { id: "checklist", label: "Checklist", icon: FileText },
  { id: "instruction", label: "Instruction", icon: BookOpen },
  { id: "sampling", label: "Sampling", icon: FlaskConical },
  { id: "line-clearance", label: "Line Clearance", icon: Settings },
  { id: "api-calculations", label: "Api Calculations", icon: Calculator },
  { id: "machine-setup", label: "Machine Setup", icon: Cog },
];

function MBMR() {
  const { t } = useTranslation();
  const subOpAlias = useSubOperationAlias();
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const currentTab = TABS.find((tab) => tab.id === activeTab);
  const ActiveIcon = currentTab.icon;

  const [inventory_list, set_inventory_list] = useState([]);
  const [parameter_list, set_parameter_list] = useState([]);
  const [sensor_list, set_sensor_list] = useState([]);
  const [actions_field_list, set_actions_field_list] = useState([]);
  const [device_list, set_device_list] = useState([]);
  const [testing_type_list, set_testing_type_list] = useState([]);
  const [department_list, set_department_list] = useState([]);
  const [clearance_type_list, set_clearance_type_list] = useState([]);
  const [cleaning_type_list, set_cleaning_type_list] = useState([]);
  const [calculation_field_list, set_calculation_field_list] = useState([]);
  const [unit_list, set_unit_list] = useState([]);
  const [machine_type_list, set_machine_type_list] = useState([]);
  const [machine_list, set_machine_list] = useState([]);
  const [resistor_list, set_resistor_list] = useState([]);
  const [app_settings, set_app_settings] = useState([]);

  // Callback function to fetch resistors when machine is selected
  const handleMachineSelect = (machine_id) => {
    if (machine_id) {
      get_register_by_machine(
        (res) => {
          if (res.data.status === "success") {
            set_resistor_list(res.data.data);
          }
        },
        { machine_id }
      );
    } else {
      set_resistor_list([]);
    }
  };

  const success_get_material_inventory = (res) => {
    if (res.data.status === "success") {
      set_inventory_list(res.data.data);
    }
  };

  const success_get_parameters = (res) => {
    if (res.data.status === "success") {
      set_parameter_list(res.data.data);
    }
  };

  const success_get_sensor_list = (res) => {
    if (res.data.status === "success") {
      set_sensor_list(res.data.data);
    }
  };

  const success_get_actionlist_data = (res) => {
    if (res.data.status === "success") {
      set_actions_field_list(res.data.data);
    }
  };

  // const success_get_device_details = (res) => {
  //   if (res.data.status === "success") {
  //     set_device_list(res.data.data);
  //   }
  // };

  const success_get_all_testing_type = (res) => {
    if (res.data.status === "success") {
      set_testing_type_list(res.data.data);
    }
  };

  const success_get_departments = (res) => {
    if (res.status === 200) {
      set_department_list(res.data.departdata);
    }
  };

  const success_get_cleaning_type_for_line = (res) => {
    if (res.data.status === "success") {
      set_cleaning_type_list(res.data.data);
    }
  };

  const success_get_clearence_type = (res) => {
    if (res.data.status === "success") {
      set_clearance_type_list(res.data.data);
    }
  };

  const success_get_calculation_field = (res) => {
    if (res.data.status === "success") {
      set_calculation_field_list(res.data.data);
    }
  };

  const success_get_unit_list = (res) => {
    if (res.data.status === "success") {
      set_unit_list(res.data.data);
    }
  };

  const success_get_machine_types = (res) => {
    if (res.data.status === "success") {
      set_machine_type_list(res.data.data);
    }
  };

  const success_get_register_machine = (res) => {
    if (res.data.status === "success") {
      set_machine_list(res.data.data);
    }
  };

  const success_get_app_settings = (res) => {
    if (res?.data?.data) {
      set_app_settings(res.data.data);
    }
  };

  useEffect(() => {
    get_material_inventory(success_get_material_inventory, {
      module_type: "prod",
    });
    get_parameters(success_get_parameters);
    get_sensor_list(success_get_sensor_list);
    get_actionlist_data(success_get_actionlist_data);
    // get_device_details(success_get_device_details);
    get_all_testing_type(success_get_all_testing_type);
    get_departments(success_get_departments);
    get_cleaning_type_for_line(success_get_cleaning_type_for_line);
    get_clearence_type(success_get_clearence_type);
    get_calculation_field(success_get_calculation_field);
    get_units(success_get_unit_list);
    get_machine_types(success_get_machine_types);
    get_register_machine(success_get_register_machine);
    get_list_of_setting(success_get_app_settings);
  }, []);

  return (
    <ConfigProvider
      theme={{
        components: {
          // Only the vertical rhythm is tightened — this form is dense. Control size and
          // label size stay at the app's defaults so the designer reads as the same product
          // as every other page (it was componentSize="large" with 16px labels).
          Form: {
            itemMarginBottom: 8,
            verticalLabelPadding: "0 0 4px",
          },
        },
      }}
    >
      <Card
        className="m-2.5 border border-gray-300 dark:border-slate-700 shadow-md"
        styles={{ body: { padding: 0 } }}
        title={
          <PageHeader
            compact
            icon={<Icons.ClipboardList size={22} />}
            title={t("MBMR Designer")}
            description={subOpAlias(
              "What the operator does in this operation \u2014 materials, monitoring, checks and instructions."
            )}
          />
        }
      >
        <div className="flex overflow-x-auto bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium text-center transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 border-b border-gray-200 dark:border-slate-700"
                  : "text-gray-500 dark:text-slate-400 hover:text-gray-700"
              }`}
            >
              {subOpAlias(tab.label)}
            </button>
          ))}
        </div>
        <div className="p-6 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 rounded-b-lg">
          <div className="flex flex-col mb-8">
            <div className="flex items-center gap-2">
              <ActiveIcon className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                {subOpAlias(currentTab.label)}
              </h2>
            </div>
            {currentTab.id === "ingredients" && (
              <Ingredients
                inventory_list={inventory_list}
                parameter_list={parameter_list}
                machine_list={machine_list}
                machine_type_list={machine_type_list}
                app_settings={app_settings}
              />
            )}
            {currentTab.id === "monitoring" && (
              <Monitoring
                sensor_list={sensor_list}
                parameter_list={parameter_list}
                operation_duration={0}
                app_settings={app_settings}
              />
            )}
            {currentTab.id === "product" && (
              <Product inventory_list={inventory_list} />
            )}
            {currentTab.id === "action-list" && (
              <ActionList actions_field_list={actions_field_list} />
            )}
            {currentTab.id === "checklist" && (
              <Checklist device_list={device_list} />
            )}
            {currentTab.id === "instruction" && <Instructions />}
            {currentTab.id === "sampling" && (
              <SamplingForm
                testing_type_list={testing_type_list}
                inventory_list={inventory_list}
                department_list={department_list}
                parameter_list={parameter_list}
              />
            )}
            {currentTab.id === "line-clearance" && (
              <LineClearanceForm
                clearance_type_list={clearance_type_list}
                cleaning_type_list={cleaning_type_list}
              />
            )}
            {currentTab.id === "api-calculations" && (
              <ApiCalculationForm
                calculation_field_list={calculation_field_list}
                unit_list={unit_list}
              />
            )}
            {currentTab.id === "machine-setup" && (
              <MachineSetup
                machine_type_list={machine_type_list}
                parameter_list={parameter_list}
              />
            )}
          </div>
        </div>
      </Card>
    </ConfigProvider>
  );
}

export default MBMR;
